import { useState, useEffect, useCallback } from "react";

const COOKIE_NAME = "orch_consent";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year
const CLARITY_ID = "vrwmr5ga8z";

function getConsent(): "accepted" | "rejected" | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  if (!match) return null;
  return match[1] === "accepted" ? "accepted" : "rejected";
}

function setConsent(value: "accepted" | "rejected") {
  document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

function loadClarity() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  // Don't load twice
  if (w.clarity) return;

  w.clarity = function (...args: unknown[]) {
    (w.clarity.q = w.clarity.q || []).push(args);
  };
  const t = document.createElement("script");
  t.async = true;
  t.src = "https://www.clarity.ms/tag/" + CLARITY_ID;
  const y = document.getElementsByTagName("script")[0];
  y.parentNode?.insertBefore(t, y);
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (consent === null) {
      setVisible(true);
    } else if (consent === "accepted") {
      loadClarity();
    }
    // If rejected, do nothing — no Clarity
  }, []);

  const accept = useCallback(() => {
    setConsent("accepted");
    loadClarity();
    setVisible(false);
  }, []);

  const reject = useCallback(() => {
    setConsent("rejected");
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6">
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-2xl shadow-black/15 border border-border-light p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-navy mb-1">Cookie preferences</p>
            <p className="text-xs text-text-secondary leading-relaxed m-0">
              We use essential cookies for authentication and platform functionality. We also use
              Microsoft Clarity for anonymised usage analytics to improve your experience.{" "}
              <a href="/privacy#cookies" className="text-accent-brand hover:underline">
                Learn more
              </a>
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={reject}
              className="px-4 py-2 text-xs font-semibold text-text-secondary border border-border-light rounded-lg hover:bg-surface transition-colors cursor-pointer bg-white"
            >
              Essential only
            </button>
            <button
              onClick={accept}
              className="px-4 py-2 text-xs font-bold text-white bg-navy rounded-lg hover:bg-navy-light transition-colors cursor-pointer border-none"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
